import React, {FC, memo, useEffect, useRef, useState} from 'react';
import {SvgIcon} from '@montana/shared/svg-icon';
import {IMapRef} from '@montana/shared/map';
import {IStoreCategories, MontanaDealerKeys} from './store-locator';
import {ScreenSizes} from '@montana/shared/responsive';
import {useMediaQuery} from 'react-responsive';
import {StoreListItem} from './store-list/store-list-item.component';
import {IEnhancedStore} from '@montana/models/dev';
import {StoreLocatorSearch} from './store-locator-search.component';
import {useRouter} from '@montana/containers/router';
import {isBrowser} from '@montana/helpers/browser.helper';
import StoreList from './store-list/store-list.component';
import MapComponent from '@montana/shared/map/map.component';
import {useStores} from '@montana/containers/stores';
import {useSettings} from '@montana/containers/settings/settings.container';
// eslint-disable-next-line no-undef
import LatLngBoundsLiteral = google.maps.LatLngBoundsLiteral;

export const StoreLocator: FC = memo(() => {
    const { replace } = useRouter();
    const { stores, isFetchingStores, getUserLocation, getStoresInMap, getStoresFromSearch, isSearchedStores } = useStores();
    const {market} = useSettings();
    const [showActiveStoreMobile, setShowActiveStoreMobile] = useState<boolean>(false);
    const [visibleStores, setVisibleStores] = useState<IEnhancedStore[]>(stores || []); // Stores available for selection, affected by search and map movement.
    const [storeCategories, setStoreCategories] = useState<IStoreCategories | undefined>();
    const [activeStore, setActiveStore] = useState<IEnhancedStore | undefined>();
    const isMobile = useMediaQuery({ maxWidth: ScreenSizes.TabletPortrait });
    const mapRef = useRef<IMapRef>(null);
    const mapContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isBrowser) {
            getUserLocation();

            const dealers: string = location.search.replace("?", "")
                .split("&")
                .filter(pair => pair?.startsWith('dealer='))
                .map(pair => pair.split('=')[1])
                .join('|')
                .toLowerCase();

            // We set the categories here to trigger the initial filtering
            setStoreCategories({
                bathroom: dealers.includes(MontanaDealerKeys.Bathroom),
                contractPartners: dealers.includes(MontanaDealerKeys.ContractPartner),
                flagship: dealers.includes(MontanaDealerKeys.ShowroomsFlagship),
                selectedProduct: dealers.includes(MontanaDealerKeys.SelectedProduct),
                fullProductRange: dealers.includes(MontanaDealerKeys.FullProductRange),
            });
        }
    }, []);

    useEffect(() => {
        if (stores) {
            filterStores();
        }
    }, [stores]);

    useEffect(() => {
        if (storeCategories && isBrowser) {
            filterStores();

            const categoryQueries: string[] = [];
            categoryQueries.push(`${storeCategories?.bathroom ? MontanaDealerKeys.Bathroom : ''}`);
            categoryQueries.push(`${storeCategories?.contractPartners ? MontanaDealerKeys.ContractPartner : ''}`);
            categoryQueries.push(`${storeCategories?.fullProductRange ? MontanaDealerKeys.FullProductRange : ''}`);
            categoryQueries.push(`${storeCategories?.selectedProduct ? MontanaDealerKeys.SelectedProduct : ''}`);
            categoryQueries.push(`${storeCategories?.flagship ? MontanaDealerKeys.ShowroomsFlagship : ''}`);

            const updatedSearch = categoryQueries.filter(q => !!q).join('&dealer=');

            // Update query params
            replace(`${location.pathname}${!!updatedSearch ? `?dealer=${updatedSearch}` : ''}`);
        }
    }, [storeCategories]);

    useEffect(() => {
        if (activeStore) {
            mapRef.current?.zoomToStore(activeStore);
            if (isMobile) {
                mapContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
            }
        }
    }, [activeStore]);

    const filterStores = () => {
        if (storeCategories && stores) {
            let filteredStores: IEnhancedStore[] = []
            if (!Object.values(storeCategories).reduce((a, c) => a || c)) {
                filteredStores = [...stores];
            } else {
                filteredStores = stores.filter((store: IEnhancedStore) => (
                    storeCategories.bathroom && store.bathroom) ||
                    (storeCategories.flagship && store.flagshipStore) ||
                    (storeCategories.fullProductRange && store.fullProductRange) ||
                    (storeCategories.contractPartners && store.contractPartners) ||
                    (storeCategories.selectedProduct && store.selectedProduct)
                );
            }

            setVisibleStores(filteredStores);
        }
    }

    const searchStores = (query?: string) => {
        if (!!query?.length && !!market?.language) {
            getStoresFromSearch(query, market?.language);
        }
    }

    const onSetActiveStore = (store: IEnhancedStore) => {
        setActiveStore(store);
        if (isMobile) {
            setShowActiveStoreMobile(true);
        }
    };

    const onBoundsChange = (worldCorners: LatLngBoundsLiteral) => {
        getStoresInMap(worldCorners);
    }

    return (
        <div className="store-locator">
            <div className="store-locator__search-wrapper">
                {!!stores?.length && <StoreLocatorSearch searchStores={searchStores} />}
                {/*{storeCategories && <StoreCategories categories={storeCategories} onChange={(categories) => setStoreCategories(categories)} />}*/}
            </div>

            <div className="store-locator__content">
                <div className="store-locator__list-container">
                    {!!stores?.length && <StoreList
                        loadingStores={isFetchingStores}
                        visibleStores={visibleStores}
                        onStoreSelect={onSetActiveStore}
                        selectedStore={activeStore}
                    />}
                </div>
                <div className="store-locator__map-container" ref={mapContainerRef}>
                    {!!stores?.length && <MapComponent
                        onStoreSelected={onSetActiveStore}
                        onBoundsChanged={onBoundsChange}
                        visibleStores={visibleStores}
                        isSearchedStores={isSearchedStores}
                        isFetchingStores={isFetchingStores}
                        ref={mapRef}
                    />}
                    {isMobile && activeStore && showActiveStoreMobile && (
                        <div className="store-locator__active-store-card">
                            <button className="store-locator__active-store-card-button"
                                onClick={() => setShowActiveStoreMobile(false)}>
                                <SvgIcon iconName="close" />
                            </button>
                            <StoreListItem
                                store={activeStore}
                                show selected={false}
                                onSelect={() => void (0)}
                                independent
                            />
                        </div>
                    )}
                </div>
            </div>
        </div >
    );
});
